import { useEffect, useState } from 'react';
import { Chip, IconButton, Popover } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Text from '../../Atoms/Text';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import {
  Commission,
  fetchCommissions,
  getCommissionSubject,
  getCommissionLoadingSubject
} from '../../../store/Commission';
import { chipStyles, getFormattedDate } from '../../../utils/helpers';
import { getActiveRunSubject, Run } from '../../../store/Run';

export const CommissionPopover = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [commission, setCommission] = useState<Commission | undefined>(undefined);
  const [commissionLoading, setCommissionLoading] = useState<boolean>(false);
  const [activeRun, setActiveRun] = useState<Run | null>(null);

  useEffect(() => {
    const subscription = getCommissionSubject().subscribe((commissions) => {
      setCommission(commissions.at(-1));
    });

    const commissionLoadingSub = getCommissionLoadingSubject().subscribe((loadingState) =>
      setCommissionLoading(loadingState)
    );
    fetchCommissions();

    const activeRunSub = getActiveRunSubject().subscribe((run) => {
      setActiveRun(run);
    });

    return () => {
      if (subscription) subscription.unsubscribe();
      if (commissionLoadingSub) commissionLoadingSub.unsubscribe();
      if (activeRunSub) activeRunSub.unsubscribe();
    };
  }, []);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      {commissionLoading && <ComponentSpinner label="loading..." />}
      {!commissionLoading && (
        <div className="rounded hover:bg-grey-2">
          <IconButton
            aria-label="info"
            className="hover:text-grey-6"
            data-testid="popover-info-button"
            onClick={handlePopoverOpen}
            sx={{
              fontSize: '.86rem',
              padding: '8px',
              ':hover': { background: 'transparent' },
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
            {activeRun ? `${activeRun.name} Run` : 'No Active Run'}
            {activeRun && (
              <Chip
                label={activeRun.status}
                size="small"
                sx={chipStyles(activeRun.status)}
                title={activeRun.status}
              />
            )}
            <MoreHorizIcon />
          </IconButton>
        </div>
      )}
      {!commissionLoading && anchorEl !== null && (
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center'
          }}>
          <div className="p-4">
            <Text
              className="pb-1"
              color="grey6"
              size="sm"
              weight="normal">
              Last RPM Sync
            </Text>
            <Text
              className="pb-1"
              size="sm"
              weight="normal">
              {commission?.createdAt && getFormattedDate(commission.createdAt, 'lll')}
            </Text>
            <Text
              className="pb-1"
              color="grey6"
              size="sm"
              weight="normal">
              Status
            </Text>
            <Text
              className="pb-1"
              size="sm"
              weight="normal">
              {commission?.status}
            </Text>
            <Text
              className="pb-1"
              color="grey6"
              size="sm"
              weight="normal">
              Requestor
            </Text>
            <Text
              className="pb-1"
              size="sm"
              weight="normal">
              {commission?.email}
            </Text>
          </div>
        </Popover>
      )}
    </>
  );
};

export default CommissionPopover;
